import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MailCheck, MailWarning, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import ResendConfirmation from '@/components/ResendConfirmation';
import { authLink, localizeNext, readPurchaseIntent } from '@/lib/purchaseIntent';

/**
 * EMAIL OWNERSHIP LINK LANDING PAGE  (/en/verify-email, /es/verificar-correo)
 *
 * The link in the email_verification mail carries a signed one-time token.
 * Everything is decided SERVER-SIDE by the verify-email function (signature,
 * expiry, single use, address unchanged). This page only shows the answer, so
 * it works from any device and without being signed in.
 */
type Phase = 'checking' | 'verified' | 'already' | 'expired' | 'used' | 'email_changed' | 'invalid' | 'error';

const VerifyEmail: React.FC = () => {
  const { path, lang } = useI18n();
  const es = lang === 'es';
  const { user, profile, refreshProfile } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [phase, setPhase] = useState<Phase>('checking');
  const [email, setEmail] = useState('');
  const ran = useRef(false);
  /** What they were buying before we asked them to confirm (if anything). */
  const [intent] = useState(() => readPurchaseIntent());

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setPhase('invalid');
      return;
    }
    (async () => {
      try {
        const { data } = await supabase.functions.invoke('verify-email', { body: { token } });
        if (data?.ok) {
          setEmail(String(data.email || ''));
          setPhase(data.already_verified ? 'already' : 'verified');
          // Remove the capability token from the address bar / history.
          window.history.replaceState(null, '', window.location.pathname);
          if (user) void refreshProfile();
          return;
        }
        const err = String(data?.error || 'error');
        setPhase((['expired', 'used', 'email_changed', 'invalid'].includes(err) ? err : 'error') as Phase);
      } catch {
        setPhase('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const ok = phase === 'verified' || phase === 'already';

  const copy: Record<Phase, { title: string; body: string }> = {
    checking: { title: es ? 'Verificando…' : 'Verifying…', body: es ? 'Un momento.' : 'One moment.' },
    verified: {
      title: es ? 'Correo verificado' : 'Email verified',
      body: es
        ? `Confirmamos que ${email || 'este buzón'} le pertenece. Su cuenta está lista.`
        : `We confirmed ${email || 'this mailbox'} belongs to you. Your account is all set.`,
    },
    already: {
      title: es ? 'Ya estaba verificado' : 'Already verified',
      body: es ? 'Este correo ya fue confirmado. No necesita hacer nada más.' : 'This email was already confirmed. Nothing else to do.',
    },
    expired: {
      title: es ? 'El enlace venció' : 'This link has expired',
      body: es ? 'Los enlaces duran 24 horas. Envíese uno nuevo abajo.' : 'Links last 24 hours. Send yourself a new one below.',
    },
    used: {
      title: es ? 'El enlace ya se usó' : 'This link was already used',
      body: es ? 'Cada enlace funciona una sola vez. Envíese uno nuevo abajo.' : 'Each link works once. Send yourself a new one below.',
    },
    email_changed: {
      title: es ? 'El correo de la cuenta cambió' : 'The account email changed',
      body: es ? 'Este enlace era para otra dirección. Envíe uno nuevo a su correo actual.' : 'This link was for a different address. Send a new one to your current email.',
    },
    invalid: {
      title: es ? 'Enlace no válido' : 'Invalid link',
      body: es ? 'El enlace está incompleto o fue modificado. Ábralo directamente desde el correo o envíese uno nuevo.' : 'The link is incomplete or was altered. Open it straight from the email, or send yourself a new one.',
    },
    error: {
      title: es ? 'No pudimos verificar' : 'We could not verify right now',
      body: es ? 'Intente de nuevo en un momento o escriba a support@giggone.com.' : 'Please try again in a moment, or email support@giggone.com.',
    },
  };

  const c = copy[phase];

  return (
    <div className="mx-auto max-w-xl px-4 py-14 sm:px-6">
      <Seo title={es ? 'Verificar correo' : 'Verify email'} description="" noindex />
      <div
        className={`rounded-2xl border-2 p-6 sm:p-8 ${
          phase === 'checking' ? 'border-slate-200 bg-white' : ok ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'
        }`}
      >
        <p className={`flex items-center gap-2 text-xl font-extrabold ${ok ? 'text-emerald-900' : phase === 'checking' ? 'text-slate-900' : 'text-amber-900'}`}>
          {phase === 'checking' ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : ok ? (
            <MailCheck className="h-6 w-6" />
          ) : (
            <MailWarning className="h-6 w-6" />
          )}
          {c.title}
        </p>
        <p className={`mt-3 text-sm leading-relaxed ${ok ? 'text-emerald-900' : 'text-slate-700'}`}>{c.body}</p>

        {!ok && phase !== 'checking' && (
          user && profile?.email ? (
            <ResendConfirmation email={profile.email} lang={lang} userId={profile.id} className="mt-5" />
          ) : (
            <p className="mt-4 text-sm text-slate-700">
              {es ? 'Inicie sesión para enviarse un enlace nuevo.' : 'Sign in to send yourself a new link.'}
            </p>
          )
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          {/*
            RESUME THE PURCHASE. Most people open this link because the lead or
            membership page asked them to. If they were mid-purchase, the ONLY
            big button takes them straight back to it (the lead page then opens
            checkout by itself) — no dashboard detour, no searching.
          */}
          {ok && intent ? (
            <Link
              to={
                user
                  ? localizeNext(intent.returnTo, path, intent.returnTo)
                  : authLink(path('/login'), localizeNext(intent.returnTo, path, intent.returnTo), intent.kind)
              }
              className="inline-flex min-h-[52px] w-full items-center justify-center rounded-xl bg-orange-500 px-5 py-3 text-base font-bold text-white hover:bg-orange-600 sm:w-auto"
            >
              {intent.kind === 'lead'
                ? es
                  ? 'Continuar con la compra del lead'
                  : 'Continue buying your lead'
                : es
                ? 'Continuar con su membresía'
                : 'Continue to your membership'}
            </Link>
          ) : (
            <Link
              to={path(user ? '/dashboard' : '/login')}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800"
            >
              {user ? (es ? 'Ir a mi panel' : 'Go to my dashboard') : es ? 'Iniciar sesión' : 'Sign in'}
            </Link>
          )}
          {!(ok && intent) && (
            <Link
              to={path('/jobs')}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl border-2 border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-white"
            >
              {es ? 'Ver proyectos' : 'Browse projects'}
            </Link>
          )}
        </div>
        {ok && intent && (
          <p className="mt-3 text-xs text-emerald-800">
            {es
              ? 'Si dejó la página del lead abierta en otra pestaña, ya continuó sola.'
              : 'If you left the lead page open in another tab, it has already continued on its own.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
